/**
 * BADGE AND STATUS PILL (P36) — reference §7 and §8.
 *
 * =========================================================================
 * THE TINTS ARE FOUR AND THE WORDS ARE ELSEWHERE
 * =========================================================================
 * §8: blue is *the agent has it and nobody is waiting on a person*, amber is
 * *a person has to act*, green is *finished*, everything else is neutral.
 * `err` is the fifth and is not a status — it is the draft-warning marker §7's
 * Badge row names, and `StatusPill` cannot take it.
 *
 * **No label is written in this file.** `web/src/lib/labels.ts` is the single
 * source for every status, review reason and draft state on screen, mirrored on
 * the SQL side by `api/src/functions/tickets/review-reasons.ts`, and
 * `p22:verify` goes red if the two drift. A kit component that invented a word
 * would be a third source.
 *
 * **P38 did the remapping** (2026-09-30). `TICKET_STATUS_BADGE` is gone: it held
 * Tailwind classes in five colour families — including `purple-50` twice and
 * `orange-50`, neither of which is in §2's tokens or means anything — and it is
 * now `TICKET_STATUS_TONE` in `labels.ts`, which names one of the four tones
 * below and no class at all. The tone a status has is vocabulary and belongs
 * beside its word; what the tone looks like is this file's, and nothing else
 * knows these five class strings.
 *
 * =========================================================================
 * `whitespace-nowrap` IS LOAD-BEARING
 * =========================================================================
 * Measured before this phase: on `/review` the status pill wrapped to **four
 * lines** and the ticket number to three, and rows were 141 px tall. A pill
 * that wraps is not a pill — it is a paragraph with a coloured background. The
 * width it then needs is P37's problem to solve in the column; refusing to wrap
 * is what makes the problem visible instead of absorbing it into the row.
 *
 * `tabular-nums` because a badge is very often a count (S2-13).
 */
import type { ReactNode } from 'react';
import type { GlyphName, Tone } from '../../lib/labels';
import { Glyph } from './glyphs';

export type { Tone };

const TONES: Record<Tone, string> = {
    neutral: 'bg-head text-muted border-hair',
    info: 'bg-info-50 text-info border-info-100',
    warn: 'bg-warn-50 text-warn-ink border-warn-edge',
    done: 'bg-brand-50 text-brand-700 border-brand-100',
    err: 'bg-err-50 text-err border-err-edge',
};

const SHAPE =
    'inline-flex items-center gap-1 whitespace-nowrap border px-2 py-0.5 text-xs font-medium tabular-nums';

export interface BadgeProps {
    tone?: Tone;
    /** A glyph. Colour never works alone (§8), so a badge that means something carries one. */
    glyph?: ReactNode;
    /**
     * The glyph by NAME (P38), which is how `lib/labels.ts` states it — and the
     * form to prefer, because passing it lets THIS component apply §8's rule
     * rather than every call site remembering it:
     *
     * **a glyph is drawn only where the tint carries meaning.** A neutral pill is
     * grey precisely because its colour is saying nothing, so there is no colour
     * for a glyph to back up and the word is already the whole statement. It is
     * also where the width is needed: the three longest status words in the app
     * are all neutral under §8's table, and a glyph plus its gap costs ~16 px of a
     * column P37 measured to the pixel.
     *
     * `glyph` (a node) still wins when it is given, for the badges whose mark is
     * not one of the seven state glyphs.
     */
    glyphName?: GlyphName;
    title?: string;
    className?: string;
    children: ReactNode;
}

/** §8's rule, in one place: meaning-carrying tint → glyph; neutral → none. */
function markFor(tone: Tone, glyph: ReactNode, glyphName?: GlyphName): ReactNode {
    if (glyph) return glyph;
    if (!glyphName || tone === 'neutral') return null;
    return <Glyph name={glyphName} />;
}

export function Badge({ tone = 'neutral', glyph, glyphName, title, className = '', children }: BadgeProps) {
    return (
        <span title={title} className={`${SHAPE} rounded-ctl ${TONES[tone]} ${className}`.trim()}>
            {markFor(tone, glyph, glyphName)}
            {children}
        </span>
    );
}

/**
 * A status. Round-ended, and restricted to the four tints §8 allows a status to
 * have — an error is not a status the DRA has.
 */
export function StatusPill({
    tone = 'neutral', glyph, glyphName, title, className = '', children,
}: Omit<BadgeProps, 'tone'> & { tone?: Exclude<Tone, 'err'> }) {
    return (
        <span title={title} className={`${SHAPE} rounded-full ${TONES[tone]} ${className}`.trim()}>
            {markFor(tone, glyph, glyphName)}
            {children}
        </span>
    );
}
